import React, { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import { Phone, Plus, ShoppingCart, X, RefreshCw, Search } from 'lucide-react';
import api from '../api/client';

export default function TwilioNumberPool() {
  const [numbers, setNumbers] = useState([]);
  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(true);

  const [showAddModal, setShowAddModal] = useState(false);
  const [showBuyModal, setShowBuyModal] = useState(false);
  const [assignTarget, setAssignTarget] = useState(null); // number object

  const [addForm, setAddForm] = useState({ phone_number: '', friendly_name: '', twilio_sid: '' });
  const [areaCode, setAreaCode] = useState('');
  const [searching, setSearching] = useState(false);
  const [availableNumbers, setAvailableNumbers] = useState([]);
  const [buying, setBuying] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [numbersRes, tenantsRes] = await Promise.all([
        api.get('/super-admin/twilio/numbers'),
        api.get('/super-admin/twilio/tenants'),
      ]);
      setNumbers(numbersRes.data.numbers || []);
      setTenants(tenantsRes.data.tenants || []);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to load numbers');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const handleAdd = async (e) => {
    e.preventDefault();
    try {
      await api.post('/super-admin/twilio/numbers', addForm);
      toast.success('Number added');
      setShowAddModal(false);
      setAddForm({ phone_number: '', friendly_name: '', twilio_sid: '' });
      load();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to add number');
    }
  };

  const handleSearch = async () => {
    if (!areaCode) return toast.error('Enter an area code');
    setSearching(true);
    try {
      const { data } = await api.get(`/super-admin/twilio/numbers/search?areaCode=${encodeURIComponent(areaCode)}`);
      setAvailableNumbers(data.available || []);
      if (!data.available?.length) toast.error('No numbers available for that area code');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Search failed');
    } finally {
      setSearching(false);
    }
  };

  const handleBuy = async (phoneNumber) => {
    if (!window.confirm(`Purchase ${phoneNumber}? This will incur charges on the Twilio account.`)) return;
    setBuying(true);
    try {
      await api.post('/super-admin/twilio/numbers/buy', { phoneNumber });
      toast.success(`${phoneNumber} purchased`);
      setShowBuyModal(false);
      setAvailableNumbers([]);
      setAreaCode('');
      load();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Purchase failed');
    } finally {
      setBuying(false);
    }
  };

  const handleDelete = async (id, phoneNumber) => {
    if (!window.confirm(`Remove ${phoneNumber} from pool? (does NOT release number from Twilio)`)) return;
    try {
      await api.delete(`/super-admin/twilio/numbers/${id}`);
      toast.success('Removed from pool');
      load();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Delete failed');
    }
  };

  const handleAssign = async (id, tenant_id) => {
    try {
      await api.post(`/super-admin/twilio/numbers/${id}/assign`, { tenant_id });
      toast.success('Assigned');
      setAssignTarget(null);
      load();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Assign failed');
    }
  };

  const handleUnassign = async (id) => {
    if (!window.confirm('Unassign this number from its tenant?')) return;
    try {
      await api.post(`/super-admin/twilio/numbers/${id}/unassign`);
      toast.success('Unassigned');
      load();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Unassign failed');
    }
  };

  return (
    <div className="p-8 max-w-6xl">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="h-10 w-10 rounded-lg bg-indigo-50 flex items-center justify-center">
            <Phone className="h-5 w-5 text-indigo-600" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Twilio Number Pool</h1>
            <p className="text-sm text-gray-500">Manage Sixty10's pool of phone numbers and assign to tenants</p>
          </div>
        </div>
        <div className="flex gap-2">
          <button onClick={load} className="text-sm bg-gray-100 hover:bg-gray-200 text-gray-700 px-3 py-2 rounded-lg font-medium flex items-center gap-1">
            <RefreshCw className="h-4 w-4" /> Refresh
          </button>
          <button onClick={() => setShowAddModal(true)} className="text-sm bg-white border border-gray-200 hover:border-gray-300 text-gray-700 px-3 py-2 rounded-lg font-medium flex items-center gap-1">
            <Plus className="h-4 w-4" /> Add Existing
          </button>
          <button onClick={() => setShowBuyModal(true)} className="text-sm bg-brand-600 hover:bg-brand-700 text-white px-3 py-2 rounded-lg font-medium flex items-center gap-1">
            <ShoppingCart className="h-4 w-4" /> Buy Number
          </button>
        </div>
      </div>

      {loading ? (
        <div className="animate-pulse space-y-2">
          {[...Array(5)].map((_, i) => <div key={i} className="h-12 bg-gray-100 rounded" />)}
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-gray-50">
              <tr>
                <th className="text-left px-4 py-2 font-medium text-gray-500">Number</th>
                <th className="text-left px-4 py-2 font-medium text-gray-500">Friendly Name</th>
                <th className="text-left px-4 py-2 font-medium text-gray-500">Status</th>
                <th className="text-left px-4 py-2 font-medium text-gray-500">Tenant</th>
                <th className="text-left px-4 py-2 font-medium text-gray-500">Purchased</th>
                <th className="text-right px-4 py-2 font-medium text-gray-500">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {numbers.length === 0 ? (
                <tr><td colSpan={6} className="px-4 py-8 text-center text-gray-400">No numbers in pool</td></tr>
              ) : numbers.map(n => (
                <tr key={n.id} className="hover:bg-gray-50">
                  <td className="px-4 py-3 font-mono text-gray-900">{n.phone_number}</td>
                  <td className="px-4 py-3 text-gray-700">{n.friendly_name || '—'}</td>
                  <td className="px-4 py-3">
                    <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                      n.status === 'assigned' ? 'bg-green-100 text-green-700' :
                      n.status === 'available' ? 'bg-blue-100 text-blue-700' :
                      'bg-gray-100 text-gray-600'
                    }`}>{n.status}</span>
                  </td>
                  <td className="px-4 py-3 text-gray-700">{n.tenant_name || '—'}</td>
                  <td className="px-4 py-3 text-gray-500 text-xs">{new Date(n.purchased_at).toLocaleDateString()}</td>
                  <td className="px-4 py-3 text-right">
                    {n.status === 'assigned' ? (
                      <button onClick={() => handleUnassign(n.id)} className="text-xs text-amber-700 hover:text-amber-900 font-medium mr-3">Unassign</button>
                    ) : (
                      <button onClick={() => setAssignTarget(n)} className="text-xs text-brand-600 hover:text-brand-800 font-medium mr-3">Assign</button>
                    )}
                    <button onClick={() => handleDelete(n.id, n.phone_number)} className="text-xs text-red-600 hover:text-red-800 font-medium">Remove</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Add Existing modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-6 w-full max-w-md">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold">Add Existing Twilio Number</h2>
              <button onClick={() => setShowAddModal(false)}><X className="h-5 w-5 text-gray-400" /></button>
            </div>
            <form onSubmit={handleAdd} className="space-y-3">
              <div>
                <label className="text-xs font-medium text-gray-600 mb-1 block">Phone Number (E.164)</label>
                <input type="text" placeholder="+18051234567" required
                  value={addForm.phone_number}
                  onChange={e => setAddForm(p => ({ ...p, phone_number: e.target.value }))}
                  className="w-full text-sm border border-gray-200 rounded-lg px-3 py-2" />
              </div>
              <div>
                <label className="text-xs font-medium text-gray-600 mb-1 block">Friendly Name</label>
                <input type="text" placeholder="T5Law Main"
                  value={addForm.friendly_name}
                  onChange={e => setAddForm(p => ({ ...p, friendly_name: e.target.value }))}
                  className="w-full text-sm border border-gray-200 rounded-lg px-3 py-2" />
              </div>
              <div>
                <label className="text-xs font-medium text-gray-600 mb-1 block">Twilio SID (optional)</label>
                <input type="text" placeholder="PN..."
                  value={addForm.twilio_sid}
                  onChange={e => setAddForm(p => ({ ...p, twilio_sid: e.target.value }))}
                  className="w-full text-sm border border-gray-200 rounded-lg px-3 py-2" />
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <button type="button" onClick={() => setShowAddModal(false)} className="text-sm px-3 py-2 border border-gray-200 rounded-lg">Cancel</button>
                <button type="submit" className="text-sm bg-brand-600 text-white px-3 py-2 rounded-lg">Add</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Buy Number modal */}
      {showBuyModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-6 w-full max-w-2xl">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold">Buy New Number</h2>
              <button onClick={() => { setShowBuyModal(false); setAvailableNumbers([]); setAreaCode(''); }}>
                <X className="h-5 w-5 text-gray-400" />
              </button>
            </div>
            <div className="flex gap-2 mb-4">
              <input type="text" placeholder="Area code e.g. 805"
                value={areaCode}
                onChange={e => setAreaCode(e.target.value)}
                className="flex-1 text-sm border border-gray-200 rounded-lg px-3 py-2" />
              <button onClick={handleSearch} disabled={searching}
                className="text-sm bg-gray-700 text-white px-3 py-2 rounded-lg font-medium flex items-center gap-1 disabled:opacity-50">
                <Search className="h-4 w-4" /> Search
              </button>
            </div>
            {availableNumbers.length > 0 && (
              <div className="max-h-96 overflow-y-auto border border-gray-200 rounded-lg">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 sticky top-0">
                    <tr>
                      <th className="text-left px-3 py-2 font-medium text-gray-500">Number</th>
                      <th className="text-left px-3 py-2 font-medium text-gray-500">Location</th>
                      <th className="text-right px-3 py-2 font-medium text-gray-500">Action</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {availableNumbers.map(n => (
                      <tr key={n.phone_number}>
                        <td className="px-3 py-2 font-mono">{n.phone_number}</td>
                        <td className="px-3 py-2 text-gray-600">{n.locality}, {n.region}</td>
                        <td className="px-3 py-2 text-right">
                          <button onClick={() => handleBuy(n.phone_number)} disabled={buying}
                            className="text-xs bg-brand-600 text-white px-3 py-1 rounded-full font-medium hover:bg-brand-700 disabled:opacity-50">
                            Buy
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Assign modal */}
      {assignTarget && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-6 w-full max-w-md">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold">Assign {assignTarget.phone_number}</h2>
              <button onClick={() => setAssignTarget(null)}><X className="h-5 w-5 text-gray-400" /></button>
            </div>
            <div className="space-y-2 max-h-96 overflow-y-auto">
              {tenants.map(t => (
                <button key={t.id} onClick={() => handleAssign(assignTarget.id, t.id)}
                  className="w-full text-left px-3 py-2 rounded-lg border border-gray-200 hover:bg-brand-50 hover:border-brand-300 text-sm">
                  <div className="font-medium text-gray-900">{t.name}</div>
                  <div className="text-xs text-gray-500">{t.slug}</div>
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
